/** @jsxImportSource @emotion/react */

import { Interpolation, Theme, css } from "@emotion/react";
import { debounce } from "lodash";
import { LegacyRef, forwardRef, useCallback } from "react";
import { theme } from "styles/theme";

type Props = {
    styles?: {
        container?: Interpolation<Theme>;
        input?: Interpolation<Theme>;
    };
    onChange?: (value: string) => void;
    onChangeDebounce?: (value: string) => void;
    type?: "default" | "disabled" | "error";
    placeholder?: string;
    defaultValue?: string;
    errorText?: string;
    id?: string;
    onKeyDown?: (e: any) => void;
    inputmode?: "none" | "numeric";
    value?: string;
    onBlur?: () => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
    onMouseDown?: (e: React.MouseEvent<HTMLParagraphElement, MouseEvent>) => void;
    onClick?: () => void;
};
const InputField = forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
    const {
        styles,
        onChange = () => {},
        type = "default",
        placeholder,
        defaultValue,
        errorText,
        onChangeDebounce = () => {},
        id,
        onKeyDown = () => {},
        inputmode = "none",
        value,
        onBlur,
        onFocus,
        onMouseDown,
        onClick,
    } = props;

    const delayChange = useCallback(
        debounce((q) => onChangeDebounce(q), 500),
        []
    );

    return (
        <div
            css={[
                css`
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                `,
                styles?.container,
            ]}
        >
            <input
                onClick={onClick}
                ref={ref}
                onBlur={onBlur}
                onFocus={onFocus}
                value={value}
                type={inputmode === "numeric" ? "number" : undefined}
                inputMode={inputmode}
                className={`select-input-${id}`}
                onChange={(e) => {
                    onChange(e.target.value);
                    delayChange(e.target.value);
                }}
                onMouseDown={onMouseDown}
                placeholder={placeholder}
                defaultValue={defaultValue}
                disabled={type === "disabled" ? true : false}
                onKeyDown={(e) => onKeyDown(e)}
                css={[
                    css`
                        display: flex;
                        padding: 14px 18px;
                        border-radius: 10px;
                        background-color: ${theme.neutral50};
                        font-size: 15px;
                        font-weight: 400px;
                        border: none;
                        :focus {
                            outline: none;
                        }
                    `,
                    styles?.input,
                    type === "disabled" && `color: ${theme.neutral200}`,
                    type === "error" && "border: 1px solid #ff6868",
                ]}
            />
            {type === "error" && errorText && (
                <p
                    css={css`
                        margin-top: 12px;
                        color: #ff6868;
                        font-size: 12px;
                        font-weight: 500;
                    `}
                >
                    {errorText}
                </p>
            )}
        </div>
    );
});

export default InputField;
